import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIncentivesEntry } from './hooks/useIncentivesEntry';
import AppDropdown from '../../../components/AppDropdown';
import AppCombobox from '../../../components/AppCombobox';
import { appToast } from '../../../components/AppToast';
import { fetchEmployeeOptions } from '../../../api/filterOptionApi';

export default function IncentivesEntryScreen() {
  const { state, actions } = useIncentivesEntry();
  const navigate = useNavigate();
  const { form, isEdit, isLoading, isSaving, dynamicOptions } = state;

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center p-16">
        <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        <p className="mt-3 text-xs font-semibold text-on-surface-variant">Loading Incentive Details...</p>
      </div>
    );
  }

  const employeeOptions = [
    { value: '', label: 'Select Employee' },
    ...(dynamicOptions.employees || []),
  ];

  const payHeadOptions = [
    { value: '', label: 'Select Pay Head' },
    ...(dynamicOptions.payHeads || []),
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header Banner */}
      <section className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Emp Incentive' : 'Create Emp Incentive'}
          </h1>
          {isEdit && form.vno && (
            <p className="text-xs font-mono font-bold text-primary mt-1">
              Voucher No: INC-{String(form.vno).padStart(3, '0')}
            </p>
          )}
        </div>

        <Link
          to="/payroll/incentives"
          className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface-variant hover:text-primary hover:bg-surface-container-low transition shadow-xs w-fit"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to List</span>
        </Link>
      </section>

      {/* 2-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Form Card (lg:col-span-8) */}
        <div className="lg:col-span-8 rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm overflow-hidden flex flex-col justify-between">
          {/* Card Header */}
          <div className="border-b border-outline-variant/20 bg-surface-container-low/40 px-8 py-4">
            <h2 className="font-display text-lg font-bold text-on-surface">Emp Incentive Entry</h2>
          </div>

          {/* Form Body */}
          <div className="p-8">
            <div className="max-w-xl mx-auto space-y-5">
              {/* Employee Combobox */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Employee <span className="text-error">*</span>
                </label>
                <div className="flex items-center gap-1.5 w-full">
                  <div className="flex-1 min-w-0">
                    <AppCombobox
                      fetchOptions={fetchEmployeeOptions}
                      value={form.emp_id ? Number(form.emp_id) : ''}
                      initialLabel={form.emp_name || ''}
                      onChange={(val) => actions.patchForm('emp_id', val)}
                      placeholder="Select Employee (AcHead Group Employees)"
                      searchPlaceholder="Search employee name or code..."
                      triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => appToast.info('Employee account heads are linked to your Chart of Accounts under Account Group Employees.')}
                    aria-label="View Employee info"
                    title="Employee Account Head Info"
                    className="h-9 w-9 rounded-lg border border-outline-variant/30 bg-surface-container-low hover:bg-surface-container-high text-on-surface-variant flex items-center justify-center transition cursor-pointer shrink-0"
                  >
                    <span className="material-symbols-outlined text-base">info</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/masters/acheads/heads/entry?group=Employees')}
                    aria-label="Create new Employee Account Head"
                    title="Create new Account Head"
                    className="h-9 w-9 rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/20 text-primary flex items-center justify-center transition cursor-pointer shrink-0 font-bold"
                  >
                    <span className="material-symbols-outlined text-base">add</span>
                  </button>
                </div>
              </div>

              {/* Voucher Date */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Date <span className="text-error">*</span>
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => actions.patchForm('date', e.target.value)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Pay Head Dropdown (Strictly Earning Heads) */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Pay Head <span className="text-error">*</span>
                </label>
                <AppDropdown
                  options={payHeadOptions}
                  value={form.pay_head_id}
                  onChange={(val) => actions.patchForm('pay_head_id', val)}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Amount Input */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Amount <span className="text-error">*</span>
                </label>
                <div className="relative w-full">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-on-surface-variant">
                    ₹
                  </span>
                  <input
                    type="number"
                    min={0.01}
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => actions.patchForm('amount', e.target.value)}
                    placeholder="0.00"
                    className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low pl-7 pr-3 text-xs font-semibold text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons Row */}
          <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-8 py-4">
            <button
              type="button"
              disabled={isSaving}
              onClick={() => actions.handleSave({ exitAfter: true })}
              className="h-9 px-6 bg-[#0047ab] hover:bg-[#003788] text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-sm">save</span>
              <span>{isSaving ? 'Saving...' : 'save & eXit'}</span>
            </button>

            {!isEdit && (
              <button
                type="button"
                disabled={isSaving}
                onClick={() => actions.handleSave({ exitAfter: false })}
                className="h-9 px-5 bg-surface-container-lowest hover:bg-surface-container-low text-primary font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/40 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Save &amp; continue
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate('/payroll/incentives')}
              className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right Written Context Panel */}
        <div className="lg:col-span-4 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-on-surface">
            <span className="material-symbols-outlined text-base text-amber-500 font-bold">
              lightbulb
            </span>
            <span>TIPS:</span>
          </div>

          <p className="text-xs text-on-surface-variant leading-relaxed font-medium">
            Record ad-hoc employee incentives, milestone bonuses, performance rewards, and sales commissions.
          </p>

          <div className="pt-3 border-t border-outline-variant/15 space-y-2">
            <h4 className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              PAYROLL INTEGRATION:
            </h4>
            <ul className="text-xs text-on-surface-variant space-y-2 list-disc list-inside leading-relaxed">
              <li>
                <strong className="text-on-surface">Pay Head:</strong> Linked directly to earning pay heads (deductions excluded) in monthly salary disbursement.
              </li>
              <li>
                <strong className="text-on-surface">Voucher Tracking:</strong> Every recorded incentive gets a sequential voucher reference (e.g. INC-001).
              </li>
              <li>
                <strong className="text-on-surface">Auto-Processing:</strong> Approved incentives for the active payroll period are merged automatically during monthly salary calculation.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
